import { Tooltip } from '@ibsys/kit';
import { ArrowRightIcon, TriangleAlertIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { StrategistTransition } from '@/entities/strategist';
import { cn } from '@/shared/lib';
import { formatTargetRate } from '@/shared/lib/utils';
import { conceptLabel, formatLiftPp, liftColorClass, RELIABLE_THRESHOLD } from './lib';

type Props = {
  transition: StrategistTransition;
  /** Нажатие на понятие — открыть его карточку */
  onConceptClick: (id: string) => void;
  /** Нажатие на строку — провалиться в диалоги перехода */
  onTransitionClick: (transition: StrategistTransition) => void;
};

/** Строка рейтинга: переход, число диалогов, доля цели и влияние. */
export const RankingRow: FC<Props> = ({ transition, onConceptClick, onTransitionClick }) => {
  const { t } = useTranslation('strategist');
  const unreliable = transition.n < RELIABLE_THRESHOLD;

  return (
    <tr
      role="button"
      tabIndex={-1}
      className={cn(
        'border-b border-border last:border-0 cursor-pointer hover:bg-muted/40',
        unreliable && 'opacity-60',
      )}
      onClick={() => onTransitionClick(transition)}
    >
      <td className="py-2 pr-4 w-full">
        <div className="flex items-center gap-1.5 text-sm text-foreground">
          <span
            role="button"
            tabIndex={-1}
            className="cursor-pointer hover:text-primary"
            onClick={(e) => {
              // Клик по понятию открывает его карточку, не проваливаясь в диалоги строки.
              e.stopPropagation();
              onConceptClick(transition.fromConceptId);
            }}
          >
            {conceptLabel(transition.fromConcept, transition.fromConceptId)}
          </span>
          <ArrowRightIcon className="size-3.5 shrink-0 text-muted-foreground" />
          <span
            role="button"
            tabIndex={-1}
            className="cursor-pointer hover:text-primary"
            onClick={(e) => {
              e.stopPropagation();
              onConceptClick(transition.toConceptId);
            }}
          >
            {conceptLabel(transition.toConcept, transition.toConceptId)}
          </span>
        </div>
      </td>
      <td className="py-2 px-2 text-right text-sm text-foreground tabular-nums whitespace-nowrap">
        <div className="flex items-center justify-end gap-1">
          {unreliable && (
            <Tooltip tooltip={t('transitions.ranking.unreliable')}>
              <TriangleAlertIcon className="size-3.5 text-muted-foreground" />
            </Tooltip>
          )}
          {transition.n}
        </div>
      </td>
      <td className="py-2 px-2 text-right text-sm text-muted-foreground tabular-nums">
        {formatTargetRate(transition.targetRate)}
      </td>
      <td className={cn('py-2 pl-2 text-right text-sm font-medium tabular-nums', liftColorClass(transition.lift))}>
        {formatLiftPp(transition.lift)}
      </td>
    </tr>
  );
};
